import { call } from "./api";

// Voorraad (docs/api.md §9.26, docs/partner-portal.md §16 step 7; screens H-v01 … H-v06): WLX asks the warehouse
// when the customer looks, and the answer comes a few seconds later. The portal shows that answer, or that there is
// none — never an older one.

export type StockStatus = "free" | "reserved" | "blocked";

export interface StockLine {
  sscc: string | null;
  palletNumber: string | null;
  owner: { code: string; name: string | null };
  article: string;
  description: string | null;
  batch: string | null;
  supplierBatch: string | null;
  bestBefore: string | null;
  quantity: number;
  unit: string | null;
  /** The unit's id in the warehouse: what a warehouse order for this article is in. */
  unitPk: number | null;
  weightKg: number | null;
  status: StockStatus;
  blockReason: string | null;
  lhc: string | null;
  receivedOn: string | null;
  receipt: string | null;
  warehouse: { code: string; name: string | null };
}

export interface StockRequest {
  id: string;
  status: "pending" | "ok" | "failed";
  requestedAt: string;
  asOf: string | null;
  /** timeout · broker · wms · not_configured · busy (not asked: many questions at once) */
  error: string | null;
  lines: StockLine[] | null;
  /** From when Vernieuwen asks again: WLX asks at most once a minute per company. */
  nextAt: string;
}

/** The answer of the last five minutes, a question under way, or a new one; `refresh` is Vernieuwen. */
export function getStock(refresh: boolean): Promise<StockRequest> {
  return call<{ request: StockRequest }>("GET", `/partner/stock${refresh ? "?refresh=1" : ""}`).then((body) => body.request);
}

/** How a question stands now. */
export function getStockRequest(id: string): Promise<StockRequest> {
  return call<{ request: StockRequest }>("GET", `/partner/stock/requests/${encodeURIComponent(id)}`).then((body) => body.request);
}

/** Excel per pallet: the pallets of this answer. */
export function stockCsvHref(id: string): string {
  return `/api/v1/partner/stock.csv?${new URLSearchParams({ request: id }).toString()}`;
}

/** The code the customer quotes when it keeps failing (H-v03): STK-TIMEOUT-5F0C. */
export function stockErrorCode(request: Pick<StockRequest, "id" | "error">): string {
  return `STK-${(request.error ?? "x").toUpperCase().replace(/[^A-Z]/g, "")}-${request.id.slice(0, 4).toUpperCase()}`;
}

// ─── Per article ───────────────────────────────────────────────────────────────────────────────────────────────

/** A best-before within this many months is short: what the customer gets warned about (W10, *< 9 mnd*). */
export const SHORT_MONTHS = 9;

/** The day `months` after `today` (YYYY-MM-DD), the end of the month when that day does not exist. */
export function addMonths(today: string, months: number): string {
  const [y, m, d] = today.split("-").map(Number) as [number, number, number];
  const target = new Date(Date.UTC(y, m - 1 + months, 1));
  const last = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(d, last));
  return target.toISOString().slice(0, 10);
}

/** Whole months from today to a best-before: 14 aug 2028 from 13 okt 2026 is 22. Past is 0. */
export function monthsUntil(bestBefore: string, today: string): number {
  const [y1, m1, d1] = today.split("-").map(Number) as [number, number, number];
  const [y2, m2, d2] = bestBefore.split("-").map(Number) as [number, number, number];
  const months = (y2 - y1) * 12 + (m2 - m1) - (d2 < d1 ? 1 : 0);
  return Math.max(0, months);
}

export function isShort(line: Pick<StockLine, "bestBefore">, today: string): boolean {
  return line.bestBefore !== null && line.bestBefore < addMonths(today, SHORT_MONTHS);
}

export interface StockArticle {
  /** Owner and article: the same code of two private labels is two articles. */
  key: string;
  article: string;
  description: string | null;
  owner: { code: string; name: string | null };
  /** The unit of the quantities: the most common one when the pallets disagree. */
  unit: string | null;
  quantity: number;
  free: number;
  reserved: number;
  blocked: number;
  /** Pallets: SSCCs, a line without one counts as a pallet of its own. */
  pallets: number;
  weightKg: number | null;
  shortestBestBefore: string | null;
  /** Pallets with a best-before shorter than nine months. */
  shortPallets: number;
  blockedPallets: number;
  warehouses: string[];
  /** Blocked first, then the shortest best-before (H-v01). */
  lines: StockLine[];
}

const STATUS_ORDER: Record<StockStatus, number> = { blocked: 0, reserved: 1, free: 2 };

function palletKey(line: StockLine, index: number): string {
  return line.sscc ?? `line-${index}`;
}

/** The answer per article, in the order of the description. */
export function groupStock(lines: StockLine[], today: string): StockArticle[] {
  const groups = new Map<string, StockLine[]>();
  for (const line of lines) {
    const key = `${line.owner.code}|${line.article}`;
    const group = groups.get(key);
    if (group) group.push(line);
    else groups.set(key, [line]);
  }
  const articles: StockArticle[] = [];
  for (const [key, group] of groups) {
    const sorted = [...group].sort(
      (a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || (a.bestBefore ?? "9999").localeCompare(b.bestBefore ?? "9999"),
    );
    const pallets = new Set(group.map(palletKey));
    const short = new Set(group.map((line, index) => (isShort(line, today) ? palletKey(line, index) : null)).filter(Boolean));
    const blockedPallets = new Set(group.map((line, index) => (line.status === "blocked" ? palletKey(line, index) : null)).filter(Boolean));
    const units = new Map<string | null, number>();
    for (const line of group) units.set(line.unit, (units.get(line.unit) ?? 0) + 1);
    const sum = (status: StockStatus) => group.filter((line) => line.status === status).reduce((total, line) => total + line.quantity, 0);
    const weights = group.map((line) => line.weightKg).filter((weight): weight is number => weight !== null);
    const bestBefores = group.map((line) => line.bestBefore).filter((day): day is string => day !== null).sort();
    const first = group[0]!;
    articles.push({
      key,
      article: first.article,
      description: group.find((line) => line.description)?.description ?? null,
      owner: first.owner,
      unit: [...units.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null,
      quantity: group.reduce((total, line) => total + line.quantity, 0),
      free: sum("free"),
      reserved: sum("reserved"),
      blocked: sum("blocked"),
      pallets: pallets.size,
      weightKg: weights.length > 0 ? weights.reduce((total, weight) => total + weight, 0) : null,
      shortestBestBefore: bestBefores[0] ?? null,
      shortPallets: short.size,
      blockedPallets: blockedPallets.size,
      warehouses: [...new Set(group.map((line) => line.warehouse.name ?? line.warehouse.code))].sort(),
      lines: sorted,
    });
  }
  return articles.sort((a, b) => (a.description ?? a.article).localeCompare(b.description ?? b.article) || a.article.localeCompare(b.article));
}

export const STOCK_VIEWS = ["all", "short", "blocked"] as const;
export type StockView = (typeof STOCK_VIEWS)[number];

export interface StockFilter {
  view: StockView;
  q?: string;
  owner?: string;
  warehouse?: string;
}

/** The articles the filters leave, each with only the pallets of the chosen warehouse. */
export function filterStock(articles: StockArticle[], filter: StockFilter, today: string): StockArticle[] {
  const q = filter.q?.trim().toLowerCase();
  const out: StockArticle[] = [];
  for (const article of articles) {
    if (filter.owner && article.owner.code !== filter.owner) continue;
    let current = article;
    if (filter.warehouse) {
      const lines = article.lines.filter((line) => (line.warehouse.name ?? line.warehouse.code) === filter.warehouse);
      if (lines.length === 0) continue;
      if (lines.length !== article.lines.length) current = groupStock(lines, today)[0]!;
    }
    if (filter.view === "short" && current.shortPallets === 0) continue;
    if (filter.view === "blocked" && current.blockedPallets === 0) continue;
    if (q) {
      const found =
        current.article.toLowerCase().includes(q) ||
        (current.description ?? "").toLowerCase().includes(q) ||
        current.lines.some(
          (line) => (line.batch ?? "").toLowerCase().includes(q) || (line.sscc ?? "").includes(q) || (line.lhc ?? "").toLowerCase() === q,
        );
      if (!found) continue;
    }
    out.push(current);
  }
  return out;
}

/** The counts on the filters: articles for Alle, pallets for the two warnings (H-v01). */
export function countStock(articles: StockArticle[]): Record<StockView, number> {
  return {
    all: articles.length,
    short: articles.reduce((total, article) => total + article.shortPallets, 0),
    blocked: articles.reduce((total, article) => total + article.blockedPallets, 0),
  };
}
